import {
    ModalContentViewProps,
    ModalFooterViewProps,
    ModalHeaderViewComponent,
    ModalViewProps,
} from './types'
import {
    modal,
    modalContainer,
    modalContent,
    modalFooter,
    modalFooterLeft,
    modalHeader,
} from './styles/ModalView'
import React from 'react'
import {styled} from '../../stitches/config'
import {useRovingTabIndex} from '../../utils/RovingTabIndex'

const StyledModal = styled('div', modal)
const StyledModalContainer = styled('div', modalContainer)
const StyledModalContent = styled('div', modalContent)
const StyledModalFooter = styled('div', modalFooter)
const StyledModalFooterLeft = styled('div', modalFooterLeft)
const StyledModalHeader = styled('div', modalHeader)
const StyledModalHeaderActions = styled('div', {marginLeft: 'auto'})

export const ModalHeader = ({
    icon: Icon,
    title,
    type,
    css,
    children,
}: ModalHeaderViewComponent): React.ReactElement => (
    <StyledModalHeader css={css} type={type}>
        {!!Icon &&
            <Icon />}
        <span>{title}</span>
        {!!children &&
            <StyledModalHeaderActions>
                {children}
            </StyledModalHeaderActions>}
    </StyledModalHeader>
)

export const ModalFooter = ({
    children, type,
}: ModalFooterViewProps): React.ReactElement => (
    <StyledModalFooter type={type}>
        {children}
    </StyledModalFooter>
)

export const ModalFooterLeft = ({children}: ModalFooterViewProps): React.ReactElement => (
    <StyledModalFooterLeft>
        {children}
    </StyledModalFooterLeft>
)

export const ModalContent = ({children}: ModalContentViewProps): React.ReactElement => (
    <StyledModalContent>
        {children}
    </StyledModalContent>
)


const ModalView = (props: ModalViewProps): React.ReactElement => {
    const {
        ariaLabelledby,
        autofocus,
        children,
        onClose,
        focusTrapListenDOMChanges,
        type,
        overlayBackground,
        selectAllFocusableElements,
        zIndex,
    } = props

    const ref = React.createRef<HTMLDivElement>()

    const onKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Escape') {
            onClose()
        }
    }

    const onClickAway = (e: React.MouseEvent<HTMLDivElement>): void => {
        // don't close modal if selecting text
        if (document.getSelection()?.type === 'Range') {
            return
        }

        onClose()
        e.stopPropagation()
    }

    const onClickInside = (e: React.MouseEvent<HTMLDivElement>): void => {
        // handle click event so that it does not propagate to parent parent component
        e.stopPropagation()
    }

    React.useEffect(() => {
        if (autofocus) {
            ref?.current?.focus?.()
        }
    }, [])

    const listRef = useRovingTabIndex({
        filter: false,
        listenToDOMChanges: focusTrapListenDOMChanges,
        nodes: undefined,
        selectAllFocusableElements,
    })

    return (
        <StyledModalContainer
            aria-labelledby={ariaLabelledby}
            aria-modal={true}
            css={{zIndex}}
            onClick={onClickAway}
            onKeyDown={onKeyDown}
            overlayBackground={overlayBackground}
            ref={ref}
            role='dialog'
            tabIndex={-1}
        >
            <StyledModal onClick={onClickInside} ref={listRef} type={type}>
                {children}
            </StyledModal>
        </StyledModalContainer>
    )
}

ModalView.defaultProps = {autofocus: true}

export default ModalView
